import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  CheckCheck,
  ExternalLink,
  Lock,
  Unlock,
  Loader2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { WpPostRow } from "@/hooks/useExistingPosts";
import type { FinalAction, FinalActionPriority } from "@/services/decisionEngine";
import type { ClusterFitLabel } from "@/services/wpClusterFitClassifier";

const ALL_ACTIONS: FinalAction[] = [
  "consolidate_301",
  "refurbish_url_preserve",
  "refurbish",
  "delete",
  "keep",
  "keep_outer",
  "monitor",
];

const ACTION_LABELS: Record<
  FinalAction,
  { label: string; icon: string; color: string; description: string; risk: "🟢" | "🟡" | "🔴" }
> = {
  keep: {
    label: "Zachowaj",
    icon: "✓",
    color: "emerald",
    risk: "🟢",
    description:
      "Wpis zostaje na WP bez zmian. CE pomija powiązany node (skip) — nie generujemy nowego artykułu, bo istniejący jest dobry.",
  },
  refurbish: {
    label: "Przepisz",
    icon: "✏️",
    color: "amber",
    risk: "🟢",
    description:
      "Content Engine wygeneruje NOWY content na ten URL. Stara wersja → backup. Slug może się zmienić (CE wybierze SEO-friendly). Rollback per wpis dostępny w Historii.",
  },
  refurbish_url_preserve: {
    label: "Przepisz (URL zostaje)",
    icon: "🔒",
    color: "violet",
    risk: "🟡",
    description:
      "Jak Przepisz, ALE URL ZACHOWANY (slug nie zmieni się). Używane gdy wpis ma SEO ruch — chronisz equity. Zalecane dla wpisów rankujących.",
  },
  keep_outer: {
    label: "Outer cluster",
    icon: "🌳",
    color: "sky",
    risk: "🟢",
    description:
      "Wpis NIE jest core dla mapy (cluster_fit=adjacent), ale OK quality — trzymamy w outer cluster. Zero zmian na WP, tylko log w wp_action_logs.",
  },
  delete: {
    label: "Usuń",
    icon: "🗑️",
    color: "red",
    risk: "🟡",
    description:
      "Soft delete — WP post → status=draft (znika z listy publicznej, nie kasujemy z bazy WP). Hard delete dopiero po 30 dniach. Reversible w WP Admin → Posts → Drafts → Publish.",
  },
  consolidate_301: {
    label: "Kanibalizacja → 301",
    icon: "🔀",
    color: "fuchsia",
    risk: "🔴",
    description:
      "Wpis kanibalizuje SEO z innym (Senuto wykryło). Merge content → 301 redirect z loser-URL na winner-URL. WYMAGA RĘCZNEJ DECYZJI winner/loser. Wpływa na SEO equity.",
  },
  monitor: {
    label: "Monitor (ręczna)",
    icon: "👁️",
    color: "zinc",
    risk: "🟢",
    description:
      "Decision Engine NIE umiał automatycznie zaklasyfikować — edge case. Najczęściej: kwalifikuje się do refurbish ale brak zmatchowanego node w mapie. Zero zmian, tylko log.",
  },
};

const PRIORITY_GROUPS: Record<FinalActionPriority, { label: string; icon: string; color: string; actions: FinalAction[] }> = {
  high: { label: "HIGH", icon: "🚨", color: "red", actions: ["consolidate_301", "refurbish_url_preserve"] },
  medium: { label: "MEDIUM", icon: "⚡", color: "amber", actions: ["refurbish", "delete"] },
  low: { label: "LOW", icon: "💤", color: "zinc", actions: ["keep", "keep_outer", "monitor"] },
};

interface Props {
  posts: WpPostRow[];
  websiteId: string;
  userId?: string | null;
  onAccept: (wpPostId: string) => void;
  onOverride: (wpPostId: string, newAction: FinalAction, reason?: string) => void;
  onUnlock: (wpPostId: string) => void;
  onBulkAccept: (action: FinalAction) => void;
  onCompute: () => void;
  isComputing?: boolean;
}

function colorBadge(color: string): string {
  const map: Record<string, string> = {
    emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    violet: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
    sky: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    red: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
    fuchsia: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-400 border-fuchsia-500/30",
    zinc: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-400 border-zinc-500/30",
  };
  return map[color] || map.zinc;
}

function clusterFitBadge(cf: ClusterFitLabel | null): string {
  if (cf === "in_scope") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
  if (cf === "adjacent") return "bg-amber-500/15 text-amber-700 dark:text-amber-400";
  if (cf === "off_topic") return "bg-red-500/15 text-red-700 dark:text-red-400";
  return "bg-zinc-500/15 text-zinc-500";
}

function qualityBadge(q: string | null): string {
  if (q === "keep") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
  if (q === "borderline") return "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400";
  if (q === "refurbish") return "bg-red-500/15 text-red-700 dark:text-red-400";
  return "bg-zinc-500/15 text-zinc-500";
}

function seoSummary(p: WpPostRow): string {
  const t = p.seo_organic_traffic ?? 0;
  const t3 = p.seo_top_3_count ?? 0;
  const t10 = p.seo_top_10_count ?? 0;
  if (t === 0 && t10 === 0) return "brak SEO";
  return `traffic:${t} top10:${t10} top3:${t3}`;
}

function ActionTile({
  action,
  posts,
  onToggle,
  onBulkAccept,
  expanded,
  onAccept,
  onOverride,
  onUnlock,
}: {
  action: FinalAction;
  posts: WpPostRow[];
  onToggle: () => void;
  onBulkAccept: () => void;
  expanded: boolean;
  onAccept: (id: string) => void;
  onOverride: (id: string, newAction: FinalAction) => void;
  onUnlock: (id: string) => void;
}) {
  const cfg = ACTION_LABELS[action];
  const total = posts.length;
  const locked = posts.filter((p) => p.final_action_locked).length;
  const pending = total - locked;

  return (
    <div className={`rounded-lg border ${colorBadge(cfg.color)} bg-card`}>
      <div className="flex items-center justify-between p-3">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 hover:text-foreground transition-colors text-left flex-1 min-w-0"
          disabled={total === 0}
          title={cfg.description}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          <span className="text-lg shrink-0">{cfg.icon}</span>
          <div className="text-left flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold">{cfg.label}</span>
              <span className="text-[10px] opacity-70">{cfg.risk}</span>
              <Badge variant="outline" className="text-[10px]">
                {total}
              </Badge>
              {locked > 0 && (
                <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600">
                  ✓ {locked}
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
              {cfg.description}
            </p>
            {pending > 0 && (
              <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 font-medium">
                {pending} do zatwierdzenia
              </p>
            )}
          </div>
        </button>
        <div className="flex items-center gap-1">
          {pending > 0 && (
            <Button
              size="sm"
              variant={action === "delete" ? "destructive" : "outline"}
              className="h-7 text-xs"
              onClick={() => {
                if (action === "delete") {
                  // Confirm dla destruktywnej akcji
                  if (
                    !window.confirm(
                      `Soft delete ${pending} wpisów (WP status=draft)?\n\nReversible w WP Admin → Posts → Drafts → Publish.\nHard delete dopiero po 30 dniach.`,
                    )
                  )
                    return;
                }
                onBulkAccept();
              }}
              title={
                action === "delete"
                  ? `Soft delete ${pending} wpisów + zaloguj`
                  : action === "keep_outer" || action === "monitor"
                    ? `Zaakceptuj + zaloguj ${pending} wpisów (zero zmian na WP)`
                    : `Zaakceptuj ${pending} wpisów (lock; wykonanie osobnym torem dla refurbish)`
              }
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              {action === "delete"
                ? `🗑 Usuń wszystkie (${pending})`
                : `Zaakceptuj wszystkie (${pending})`}
            </Button>
          )}
        </div>
      </div>
      {expanded && total > 0 && (
        <div className="border-t px-3 py-2 space-y-1.5 max-h-[480px] overflow-y-auto">
          {posts.map((p) => (
            <div
              key={p.id}
              className="rounded border bg-background/60 p-2 space-y-1.5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {p.wp_url ? (
                    <a
                      href={p.wp_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <span className="line-clamp-1">{p.title}</span>
                      <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                    </a>
                  ) : (
                    <span className="text-xs font-medium line-clamp-1">{p.title}</span>
                  )}
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <Badge className={`text-[9px] ${qualityBadge(p.quality_recommendation)}`}>
                      Q:{p.quality_recommendation ?? "?"}
                    </Badge>
                    <Badge className={`text-[9px] ${clusterFitBadge(p.cluster_fit)}`}>
                      C:{p.cluster_fit ?? "?"}
                    </Badge>
                    <Badge variant="outline" className="text-[9px]">
                      {seoSummary(p)}
                    </Badge>
                    {p.seo_has_cannibalization && (
                      <Badge className="text-[9px] bg-fuchsia-500/15 text-fuchsia-700">
                        cannib
                      </Badge>
                    )}
                    {p.final_action_locked && (
                      <Badge className="text-[9px] bg-emerald-500/15 text-emerald-700">
                        <Lock className="h-2.5 w-2.5 mr-0.5" />
                        locked
                      </Badge>
                    )}
                    {p.final_action_user_override && (
                      <Badge className="text-[9px] bg-violet-500/15 text-violet-700">
                        override
                      </Badge>
                    )}
                  </div>
                  {p.final_action_reason && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      <strong>Powód:</strong> {p.final_action_reason}
                    </p>
                  )}
                  {/* Co po Zatwierdź — explicit dla usera */}
                  <div className="mt-1.5 rounded border border-border/40 bg-muted/30 p-1.5 text-[10px] leading-snug">
                    <div className="flex items-start gap-1">
                      <span className="font-semibold text-foreground/90 shrink-0">
                        {cfg.icon} Po &quot;Zatwierdź&quot;:
                      </span>
                      <span className="text-muted-foreground">{cfg.description}</span>
                    </div>
                    <div className="flex items-start gap-1 mt-1 pt-1 border-t border-border/30">
                      <span className="font-semibold text-foreground/70 shrink-0">
                        🔧 Alternatywy:
                      </span>
                      <span className="text-muted-foreground">
                        Override → Zmień na {action === "monitor" ? (
                          <>
                            <em>Przepisz</em> (jeśli chcesz refurbish), <em>Usuń</em> (off-topic),{" "}
                            <em>Outer cluster</em> (zostaw bez zmian) lub <em>Zachowaj</em>.
                          </>
                        ) : action === "delete" ? (
                          <>
                            <em>Monitor</em> (jeśli się wahasz), <em>Outer cluster</em> (jeśli ma
                            wartość), <em>Przepisz</em> (jeśli wart ratowania).
                          </>
                        ) : action === "refurbish" ? (
                          <>
                            <em>Przepisz (URL zostaje)</em> jeśli wpis ma SEO ruch (chronisz
                            equity), lub <em>Zachowaj</em> jeśli przemyślałeś.
                          </>
                        ) : (
                          <>inną akcję z dropdownu obok.</>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0 items-end">
                  {!p.final_action_locked ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] px-2"
                      onClick={() => onAccept(p.id)}
                    >
                      <Check className="h-2.5 w-2.5 mr-0.5" />
                      Zatwierdź
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-[10px] px-2"
                      onClick={() => onUnlock(p.id)}
                      title="Odblokuj (wróć do auto-recompute)"
                    >
                      <Unlock className="h-2.5 w-2.5 mr-0.5" />
                      Odblokuj
                    </Button>
                  )}
                  <Select
                    onValueChange={(v) => onOverride(p.id, v as FinalAction)}
                  >
                    <SelectTrigger
                      className="h-6 text-[10px] px-2 w-[150px]"
                      title="Zmień akcję dla tego wpisu na inną — auto-lock=true (Decision Engine już nie nadpisze)"
                    >
                      <SelectValue placeholder="Zmień na..." />
                    </SelectTrigger>
                    <SelectContent className="max-w-[480px]">
                      {ALL_ACTIONS.filter((a) => a !== action).map((a) => {
                        const cfg = ACTION_LABELS[a];
                        return (
                          <SelectItem key={a} value={a} className="text-xs items-start py-2">
                            <div className="flex flex-col gap-0.5 max-w-[440px]">
                              <div className="font-semibold flex items-center gap-1.5">
                                <span>{cfg.icon}</span>
                                <span>{cfg.label}</span>
                                <span className="text-[10px] opacity-60">{cfg.risk}</span>
                              </div>
                              <p className="text-[10px] text-muted-foreground leading-snug whitespace-normal">
                                {cfg.description}
                              </p>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ActionsPanel({
  posts,
  websiteId: _websiteId,
  userId: _userId,
  onAccept,
  onOverride,
  onUnlock,
  onBulkAccept,
  onCompute,
  isComputing,
}: Props) {
  const [expanded, setExpanded] = useState<Set<FinalAction>>(new Set());

  const grouped = useMemo(() => {
    const by: Record<FinalAction, WpPostRow[]> = {
      keep: [],
      refurbish: [],
      refurbish_url_preserve: [],
      keep_outer: [],
      delete: [],
      consolidate_301: [],
      monitor: [],
    };
    for (const p of posts) {
      if (p.final_action && by[p.final_action]) {
        by[p.final_action].push(p);
      }
    }
    return by;
  }, [posts]);

  // "Locked but not yet executed" — banner pokazuje sie TYLKO gdy jest cos
  // nowego do wykonania. Filter: locked=true AND action_executed_at=NULL.
  // Po wykonaniu wpActionProcessor ustawia action_executed_at=now() →
  // banner znika dla tego wpisu.
  const lockedDeletes = grouped.delete.filter(
    (p) => p.final_action_locked && !p.action_executed_at,
  );
  const lockedKeepOuter = grouped.keep_outer.filter(
    (p) => p.final_action_locked && !p.action_executed_at,
  );
  const lockedMonitor = grouped.monitor.filter(
    (p) => p.final_action_locked && !p.action_executed_at,
  );
  // Statystyki dla pełnego kontekstu w banner
  const totalDeletesAccepted = grouped.delete.filter((p) => p.final_action_locked).length;
  const totalKeepOuterAccepted = grouped.keep_outer.filter((p) => p.final_action_locked).length;
  const totalMonitorAccepted = grouped.monitor.filter((p) => p.final_action_locked).length;
  const executedDeletes = totalDeletesAccepted - lockedDeletes.length;
  const executedKeepOuter = totalKeepOuterAccepted - lockedKeepOuter.length;
  const executedMonitor = totalMonitorAccepted - lockedMonitor.length;
  const notYetComputed = posts.filter((p) => !p.final_action && p.cluster_fit).length;

  const toggle = (action: FinalAction) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(action)) next.delete(action);
      else next.add(action);
      return next;
    });
  };

  return (
    <>
      <Card className="border-violet-500/30">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                🎬 Akcje (Decision Engine)
              </CardTitle>
              <CardDescription>
                Deterministyczny silnik łączy 4 wymiary (Quality + SEO + Scope + Cannib) w
                jedną z 7 finalnych akcji. Zaakceptowane akcje (locked) są chronione przed
                auto-recompute.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={onCompute}
                disabled={isComputing}
              >
                {isComputing ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Licze...
                  </>
                ) : (
                  "Przelicz akcje"
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {notYetComputed > 0 && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
              <AlertTriangle className="h-4 w-4 inline mr-1 text-amber-600" />
              <strong>{notYetComputed}</strong> wpisów nie ma jeszcze obliczonej
              akcji. Kliknij "Przelicz akcje" aby uruchomić Decision Engine.
            </div>
          )}

          {/* Info dla refurbish — leci osobno przez publish_queue (Publikuj na mapie) */}
          {(grouped.refurbish.filter((p) => p.final_action_locked).length > 0 ||
            grouped.refurbish_url_preserve.filter((p) => p.final_action_locked).length > 0) && (
            <div className="rounded-md border border-orange-500/30 bg-orange-500/5 p-3 text-xs">
              ✏️ <strong>Refurbish</strong>: zaakceptowane wpisy są oznaczone na nodach jako{" "}
              <code>refurbish_approved=true</code>. Nowy content wygeneruje się przy następnym
              uruchomieniu Content Engine na mapie i opublikuje przez publish_queue (UPDATE
              istniejący WP post). Tu nic nie klikasz — wykonanie idzie osobnym torem.
            </div>
          )}

          {/* Counter dla user'a co już zostało wykonane — informacyjne, bez przycisku */}
          {(executedDeletes > 0 || executedKeepOuter > 0 || executedMonitor > 0) && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs flex items-center gap-3 flex-wrap">
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                ✓ Wykonane:
              </span>
              {executedDeletes > 0 && (
                <span>🗑 {executedDeletes} usuniętych (WP status=draft)</span>
              )}
              {executedKeepOuter > 0 && (
                <span>🌳 {executedKeepOuter} outer cluster (zalogowane)</span>
              )}
              {executedMonitor > 0 && <span>👁 {executedMonitor} monitor (zalogowane)</span>}
            </div>
          )}

          {(["high", "medium", "low"] as FinalActionPriority[]).map((priority) => {
            const group = PRIORITY_GROUPS[priority];
            const hasAny = group.actions.some((a) => grouped[a].length > 0);
            if (!hasAny) return null;
            return (
              <div key={priority} className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <span className="text-base">{group.icon}</span>
                  <span>{group.label}</span>
                </div>
                <div className="grid gap-2">
                  {group.actions.map((action) => {
                    if (grouped[action].length === 0) return null;
                    return (
                      <ActionTile
                        key={action}
                        action={action}
                        posts={grouped[action]}
                        onToggle={() => toggle(action)}
                        onBulkAccept={() => onBulkAccept(action)}
                        expanded={expanded.has(action)}
                        onAccept={onAccept}
                        onOverride={(id, newAction) => onOverride(id, newAction, "Override przez UI")}
                        onUnlock={onUnlock}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}

          {posts.filter((p) => p.final_action).length === 0 && notYetComputed === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              Brak wpisów. Najpierw pobierz wpisy + przeprowadź klasyfikację scope.
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
}
